import { QueryBuilderService } from '../../shared/query-builder/query-builder.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { FieldConfigurationService } from '../../core/service/field-configuration.service';
import { Component, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { FieldConfigurationEdit, FieldConfigurationEditOptions } from '../model/fieldConfigurationEdit';
import { Validators, UntypedFormBuilder, UntypedFormGroup, UntypedFormArray, ValidationErrors, UntypedFormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { merge, reject } from 'lodash';
import { TabStripComponent } from '@progress/kendo-angular-layout';
import { DictionaryService } from 'src/app/core/service/dictionary.service';
import { Observable } from 'rxjs';
import { RuleValidators } from 'src/app/core/validators/rule.validators';
import { sortBy } from 'lodash';

@Component({
  standalone: false,
  selector: 'app-field-configuration-edit',
  templateUrl: './field-configuration-edit.component.html',
  styleUrls: ['./field-configuration-edit.component.scss']
})
export class FieldConfigurationEditComponent
{
  @ViewChild('tabstrip') public tabstrip: TabStripComponent;

  @Input() public isNew = false;

  @Output() public cancelling: EventEmitter<any> = new EventEmitter();
  @Output() public save: EventEmitter<FieldConfigurationEdit> = new EventEmitter();

  public hiddenOptionRequired = true;
  public editForm: any;
  public options: UntypedFormArray;
  public editFormActive = false;
  public listFieldTypes: Array<{ text: string; value: string }> = [
    { text: 'String', value: 'String' },
    { text: 'Number', value: 'Number' },
    { text: 'Select', value: 'Category' },
    { text: 'Date', value: 'Date' },
    { text: 'Time', value: 'Time' },
    { text: 'Boolean', value: 'Boolean' }
  ];

  public mainSections: any[];
  public subSections: any[];
  public codeSets: any[];

  private fieldConfigurationEdit: FieldConfigurationEdit;

  constructor(
    private translate: TranslateService,
    private fb: UntypedFormBuilder,
    private fieldConfigurationService: FieldConfigurationService,
    private messageHandlerService: MessageHandlerService,
    private queryBuiderService: QueryBuilderService,
    private dictionaryService: DictionaryService,
    private ruleValidators: RuleValidators)
  {
    // Set the listFieldTypes from localization
    this.translate.get('FIELD-TYPES').subscribe({
      next: (res: string) =>
      {
        const splitted = res.split(',');
        for (let i = 0; i < splitted.length; i++)
        {
          if (this.listFieldTypes.length > i)
          {
            this.listFieldTypes[i].text = splitted[i];
          }
        }
        this.listFieldTypes = sortBy(this.listFieldTypes, ['text']);
      }
    });

    // Set the mainSections from localization
    this.mainSections = this.queryBuiderService.mainSections;
    this.subSections = this.queryBuiderService.subSections;

    // Load CodeSets
    this.dictionaryService.getCachedCodeSets().subscribe({
      next: (data) =>
      {
        this.codeSets = data;
      },
      error: (error) =>
      {
        this.messageHandlerService.displayError(error);
      }
    });
  }

  public get type(): UntypedFormControl
  {
    return this.editForm.get('type');
  }

  public get name(): UntypedFormControl
  {
    return this.editForm.get('name');
  }

  public get codeSet(): UntypedFormControl
  {
    return this.editForm.get('codeSet');
  }

  public get section(): UntypedFormControl
  {
    return this.editForm.get('section');
  }

  public get subSection(): UntypedFormControl
  {
    return this.editForm.get('subSection');
  }

  public get xpath(): UntypedFormControl
  {
    return this.editForm.get('xpath');
  }

  @Input() public set model(fieldConfiguration: FieldConfigurationEdit)
  {
    if (!this.isNew && fieldConfiguration !== undefined)
    {
      this.fieldConfigurationService.existsFieldConfiguration(fieldConfiguration.id).subscribe({
        next: (inUseResponse) =>
        {
          this.initModel(fieldConfiguration, inUseResponse);
        },
        error: (errorResponse) =>
        {
          this.messageHandlerService.displayError(errorResponse);
          this.initModel(fieldConfiguration, false);
        }
      });
    }
    else
    {
      this.initModel(fieldConfiguration, false);
    }
  }

  public onSave(e: Event): void
  {
    e.preventDefault();
    const objectToPersist = merge(this.fieldConfigurationEdit, this.editForm.value);

    if (this.editForm.get('codeSet').value === undefined)
    {
      objectToPersist.codeSet = null;
    }

    // Ensure we have options defined for category type
    if (objectToPersist.type === 'Category')
    {
      if (objectToPersist.options.length === 0 && !objectToPersist.codeSet)
      {
        this.hiddenOptionRequired = false;
        this.tabstrip.selectTab(1);
        return;
      }
    }

    if (objectToPersist.type !== 'Category' || objectToPersist.codeSet)
    {
      objectToPersist.options = [];
    }

    this.save.emit(objectToPersist);
    this.editFormActive = false;
  }

  public onCancel(e: Event): void
  {
    e.preventDefault();
    this.closeEditForm();
  }

  public addOption(id: number = null, name = '', value = '', codeSet: any = null): void
  {
    this.editForm.controls.options.push(this.initOption(id, name, value, codeSet));
    this.hiddenOptionRequired = true;
    if (!id) // This is a new entry and must attach change events
    {
      this.setCodeSetChanges();
    }
  }

  public removeOption(i: number, id: number, value: string): void
  {
    const control = this.editForm.controls.options;
    // If this id was already persisted, we must remove
    this.fieldConfigurationService.fieldConfigurationInUse(this.fieldConfigurationEdit.id, value).subscribe({
      next: (response) =>
      {
        if (!response)
        {
          if (id > 0)
          {
            const rejectedId: any = {};
            rejectedId.id = id;
            this.fieldConfigurationEdit.options = reject(this.fieldConfigurationEdit.options, rejectedId);
          }
          control.removeAt(i);
          this.editForm.markAsDirty();
        }
        else
        {
          this.messageHandlerService.displayError('FIELD-CONFIGURATION-IN-USE-CANNOT-DELETE');
        }
      }
    });
  }

  public isValueVisible(i: number): boolean
  {
    const obj = this.editForm.controls.options.controls[i].controls.codeSet;
    return !obj.value || obj.value === undefined;
  }

  private setXPathValidator(type: string): void
  {
    if (type === 'Boolean')
    {
      this.editForm.get('xpath').setValidators([Validators.required]);
    }
    else
    {
      this.editForm.get('xpath').setValidators([
        Validators.required,
        this.ruleValidators.validateXpath('$operator'),
        this.ruleValidators.validateXpath('$value')
      ]);
    }
    this.editForm.get('xpath').updateValueAndValidity();
  }

  private setSubSectionValidator(mainSection: string): void
  {
    if (mainSection === 'ClinicalHistory')
    {
      this.editForm.get('subSection').setValidators([Validators.required]);
    }
    else
    {
      this.editForm.get('subSection').setValidators([]);
      this.editForm.get('subSection').value = null;
    }
    this.editForm.get('subSection').updateValueAndValidity();
  }

  private setCodeSetChanges(): void
  {
    let rowId = 0;
    this.editForm.controls.options.controls.forEach(option =>
    {
      // Assign sequential rowId
      option.controls.rowId.value = rowId;
      rowId++;

      // Set value changes handler on code set changes
      option.valueChanges.subscribe({
        next: (data: FieldConfigurationEditOptions) =>
        {
          const rowData = this.editForm.controls.options.controls.find((x: any) => x.controls.rowId.value === data.rowId);
          if (rowData)
          {
            if (rowData.value.codeSet === undefined)
            {
              rowData.controls.codeSet.setValue(null);
              rowData.controls.value.setValue('');
            }
            else if (rowData.value.codeSet)
            {
              if (rowData.value.codeSet.id !== rowData.controls.value.value)
              {
                rowData.controls.value.setValue(`CODESET^${rowData.value.codeSet.id}`);
              }
            }
          }
        }
      });
    });
  }

  private closeEditForm(): void
  {
    this.editFormActive = false;
    this.cancelling.emit();
  }

  private initModel(fieldConfiguration: FieldConfigurationEdit, inUseConfiguration: boolean)
  {
    if (fieldConfiguration === undefined)
    {
      return;
    }

    // Set the current field configuration name used for name async validators
    const previousFieldConfigurationName = this.isNew ? '' : fieldConfiguration.name;

    // Set form
    this.editForm = this.fb.group({
      id: [null],
      name: [
        { value: '', disabled: false },
        {
          validators: [Validators.required],
          asyncValidators: (): Observable<ValidationErrors | null> =>
            this.ruleValidators.validateFieldConfigurationNameUnique(this.editForm.get('name'), previousFieldConfigurationName)
        }
      ],
      type: [{ value: '', disabled: inUseConfiguration }, Validators.required],
      section: [{ value: '', disabled: inUseConfiguration }, Validators.required],
      subSection: [{ value: '', disabled: inUseConfiguration },
      (fieldConfiguration && fieldConfiguration.section) === 'ClinicalHistory' ? Validators.required : ''],
      xpath: [{ value: '', disabled: false }],
      active: { value: true, disabled: inUseConfiguration },
      codeSet: [{ value: '', disabled: inUseConfiguration }],
      options: this.fb.array([])
    });

    if (fieldConfiguration && fieldConfiguration.options)
    {
      fieldConfiguration.options.forEach(data =>
      {
        this.addOption(data.id, data.name, data.value, data.codeSet);
      });
    }

    this.editForm.reset(fieldConfiguration);
    this.fieldConfigurationEdit = fieldConfiguration;
    this.editFormActive = fieldConfiguration !== undefined;

    this.setCodeSetChanges();

    // Subscribe on section changes to handle subSection requirement
    this.editForm.get('section').valueChanges.subscribe({
      next: (mainSection: string) =>
      {
        this.setSubSectionValidator(mainSection);
      }
    });

    this.editForm.get('type').valueChanges.subscribe({
      next: (type: string) =>
      {
        this.setXPathValidator(type);
      }
    });

    this.setSubSectionValidator(fieldConfiguration.section);
    this.setXPathValidator(fieldConfiguration.type);
  }

  private initOption(id = 0, name: string, value: string, codeSet: any): UntypedFormGroup
  {
    const group: any = {};
    group.rowId = null;
    group.id = [id];
    group.name = [name, Validators.required];
    group.value = [value, Validators.required];
    group.codeSet = codeSet;
    return this.fb.group(group);
  }
}
